<script lang="ts">
	interface Address {
		street: string;
		suite?: string;
		city: string;
		state: string;
		zip: string;
		country?: string;
	}

	interface Props {
		entityName: string;
		email: string;
		phone: string;
		address: Address;
		lastUpdated: string;
	}

	let { entityName, email, phone, address, lastUpdated }: Props = $props();

	interface ListItem {
		title: string;
		content: string;
	}

	interface Subsection {
		title: string;
		content: string;
		list?: (string | ListItem)[];
		footer?: string;
	}

	interface Section {
		title: string;
		data: string;
		subsections?: Subsection[];
	}

	const sections: Section[] = [
		{
			title: `1. ACCEPTANCE OF TERMS`,
			data: `By accessing or using the website, services, or communications offered by ${entityName} ("we," "us," or "our"), you agree to be bound by these Terms & Conditions ("Terms"). If you do not agree to these Terms, you may not access or use our Services. These Terms apply to all visitors, users, and others who access or use the Services.`
		},
		{
			title: `2. USE OF SERVICES`,
			data: `You agree to use our Services only for lawful purposes and in a way that does not infringe the rights of, restrict, or inhibit anyone else's use and enjoyment of the Services. You agree not to:`,
			subsections: [
				{
					title: "Prohibited Conduct",
					content: "",
					list: [
						"Use the Services in any way that violates any applicable federal, state, local, or international law or regulation",
						"Attempt to gain unauthorized access to any portion of the Services or any related systems or networks",
						"Use any robot, spider, or other automatic device to access the Services for any purpose without our written permission",
						"Introduce any viruses, trojan horses, worms, logic bombs, or other harmful material",
						`Impersonate or attempt to impersonate ${entityName}, an employee, another user, or any other person or entity`
					]
				}
			]
		},
		{
			title: `3. SMS AND TEXT MESSAGING COMMUNICATIONS`,
			data: `By providing your phone number to ${entityName} through any of our forms, websites, or other channels, you expressly consent to receive calls, emails, and text (SMS) messages from ${entityName} regarding your inquiry, our services, properties, appointments, follow-ups, marketing, and other related communications. Consent is not a condition of any purchase, service, or investment.`,
			subsections: [
				{
					title: "Message Frequency and Rates",
					content: `Message frequency may vary depending on your interaction with us and the type of communications you receive. Standard message and data rates may apply based on your mobile carrier plan. ${entityName} does not charge for the messages themselves, but your wireless carrier may charge for sending or receiving text messages.`
				},
				{
					title: "Opt-Out Instructions",
					content: `You may opt out of SMS messages from ${entityName} at any time by replying "STOP" to any text message you receive from us. After replying "STOP," you will receive one final message confirming that you have been unsubscribed. After this confirmation, you will no longer receive SMS messages from us. If you wish to rejoin, you can reply "START" to begin receiving messages again, or contact us directly.`
				},
				{
					title: "Help and Support",
					content: `For help or assistance with our SMS program, reply "HELP" to any message you receive from us, or contact us at ${email} or ${phone}. You may also visit our Privacy Policy for more information on how we handle your data.`
				},
				{
					title: "Carrier Disclaimer",
					content:
						"Carriers, including but not limited to AT&T, T-Mobile, Verizon, Sprint, and others, are not liable for delayed or undelivered messages. Delivery of messages is subject to effective transmission from your wireless carrier and is not guaranteed."
				},
				{
					title: "Privacy",
					content:
						"We respect your privacy and will not sell, rent, or share your mobile phone number or SMS opt-in data with third parties for their marketing purposes. For more details on how we collect, use, and protect your information, please review our Privacy Policy."
				}
			]
		},
		{
			title: `4. INTELLECTUAL PROPERTY`,
			data: `All content on our Services, including text, graphics, logos, images, photographs, audio, video, software, and the compilation thereof, is the property of ${entityName} or its content suppliers and is protected by United States and international copyright, trademark, and other intellectual property laws. You may not copy, reproduce, modify, distribute, transmit, display, perform, publish, license, create derivative works from, transfer, or sell any information, software, products, or services obtained from our Services without our prior written consent.`
		},
		{
			title: `5. USER SUBMISSIONS`,
			data: `Any information, materials, or communications you submit to us through the Services — including inquiries, property information, comments, suggestions, or feedback — are non-confidential. By submitting any such material, you grant ${entityName} a non-exclusive, royalty-free, perpetual, irrevocable, and fully sublicensable right to use, reproduce, modify, adapt, publish, translate, distribute, and display such material throughout the world in any media, solely in connection with the operation of our business.`
		},
		{
			title: `6. PROPERTY LISTINGS AND INFORMATION`,
			data: `Property listings, descriptions, photographs, pricing, availability, and other information presented on our Services are provided for general informational purposes only. While we strive to keep this information accurate and up to date, we make no representations or warranties of any kind, express or implied, about the completeness, accuracy, reliability, suitability, or availability of any property listing or related information. All property details are subject to change, prior sale, lease, or withdrawal without notice. You should independently verify any information that is important to you before relying on it.`
		},
		{
			title: `7. THIRD-PARTY LINKS AND SERVICES`,
			data: `Our Services may contain links to third-party websites, applications, or services that are not owned or controlled by ${entityName}. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party websites or services. You acknowledge and agree that ${entityName} shall not be responsible or liable, directly or indirectly, for any damage or loss caused or alleged to be caused by or in connection with the use of or reliance on any such content, goods, or services available on or through any such third-party websites or services.`
		},
		{
			title: `8. DISCLAIMERS`,
			data: `THE SERVICES ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, ${entityName.toUpperCase()} DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. ${entityName.toUpperCase()} DOES NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED, TIMELY, SECURE, OR ERROR-FREE, OR THAT ANY INFORMATION OBTAINED THROUGH THE SERVICES WILL BE ACCURATE OR RELIABLE.`
		},
		{
			title: `9. LIMITATION OF LIABILITY`,
			data: `TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL ${entityName.toUpperCase()}, ITS AFFILIATES, OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, SUPPLIERS, OR LICENSORS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM (I) YOUR ACCESS TO OR USE OF OR INABILITY TO ACCESS OR USE THE SERVICES; (II) ANY CONDUCT OR CONTENT OF ANY THIRD PARTY ON THE SERVICES; OR (III) UNAUTHORIZED ACCESS, USE, OR ALTERATION OF YOUR TRANSMISSIONS OR CONTENT.`
		},
		{
			title: `10. INDEMNIFICATION`,
			data: `You agree to defend, indemnify, and hold harmless ${entityName} and its affiliates, officers, directors, employees, agents, and representatives from and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising out of or relating to your violation of these Terms or your use of the Services, including, but not limited to, any use of the Services' content, services, and products other than as expressly authorized in these Terms.`
		},
		{
			title: `11. GOVERNING LAW AND JURISDICTION`,
			data: `These Terms shall be governed by and construed in accordance with the laws of the State of Texas, without regard to its conflict of law provisions. You agree that any legal action or proceeding arising out of or relating to these Terms or your use of the Services shall be brought exclusively in the state or federal courts located in Victoria County, Texas, and you consent to the personal jurisdiction of such courts.`
		},
		{
			title: `12. CHANGES TO THESE TERMS`,
			data: `We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will provide notice prior to any new terms taking effect by updating the "Last updated" date at the top of these Terms. What constitutes a material change will be determined at our sole discretion. By continuing to access or use our Services after any revisions become effective, you agree to be bound by the revised Terms.`
		},
		{
			title: `13. CONTACT US`,
			data: `If you have any questions about these Terms & Conditions, please contact us:`,
			subsections: [
				{
					title: "Mailing Address",
					content: [
						entityName,
						address.street,
						address.suite,
						`${address.city}, ${address.state} ${address.zip}`,
						address.country ?? "United States"
					]
						.filter(Boolean)
						.join("\n")
				},
				{
					title: "Email",
					content: email
				},
				{
					title: "Phone",
					content: phone
				}
			]
		}
	];
</script>

<div class="mx-auto flex w-[380px] flex-col gap-4 p-10 lg:w-[1140px]">
	<h1 class="text-3xl font-bold">Terms & Conditions</h1>
	<p class="text-sm">Last updated {lastUpdated}</p>
	<p class="text-sm">
		These Terms & Conditions ("Terms") govern your access to and use of the websites, services,
		and communications provided by {entityName} ("we," "us," or "our"), including any text (SMS)
		and email messages we may send to you. Please read these Terms carefully before using our Services.
		By accessing or using our Services, you agree to be bound by these Terms and our Privacy Policy.
	</p>

	<h2 class="text-2xl font-bold">Table of Contents</h2>
	<ul class="list-disc pl-4">
		{#each sections as section, index}
			<ul class="text-blue-700">
				<a href={`#${(index + 1).toString()}`}>{section.title}</a>
			</ul>
		{/each}
	</ul>

	{#each sections as section, index}
		<section id={(index + 1).toString()} class="flex flex-col gap-4">
			<h3 class="text-xl font-bold">{section.title}</h3>
			<p>{section.data}</p>

			{#if section.subsections}
				{#each section.subsections as subsection}
					<div class="ml-4">
						<h4 class="text-lg font-bold">{subsection.title}</h4>
						<p class="whitespace-pre-line">{subsection.content}</p>

						{#if subsection.list}
							<ul class="ml-6 list-disc">
								{#each subsection.list as item}
									{#if typeof item === "string"}
										<li>{item}</li>
									{:else}
										<li>
											<span class="font-bold italic">{item.title}:</span>
											{item.content}
										</li>
									{/if}
								{/each}
							</ul>
						{/if}

						{#if subsection.footer}
							<p class="mt-4">{subsection.footer}</p>
						{/if}
					</div>
				{/each}
			{/if}
		</section>
	{/each}
</div>
